import React, { ReactElement } from 'react';
import { Container, createStyles, IconButton, makeStyles, Theme } from '@material-ui/core';
import { Menu as MenuIcon } from '@material-ui/icons';
import { AgentNetServices } from '@fluentsms/agentnet-web-icons';
import { SearchBarRow } from './SearchBarRow';
import { useHistory } from 'react-router-dom';
import useBaseRoute from 'features/help/hooks/useBaseRoute';
import { ContentfulErrorContext } from 'features/help/hooks/ContentfulErrorContext';
import { useMobileNav } from 'features/help/hooks/MobileNavContext';
import { HELP_ROUTE } from 'features/help/constants';
import { TopicCollection } from 'features/help/types';

type TopicPageHeaderProps = {
  topics?: TopicCollection | null;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(2, 0),
      position: 'sticky',
      top: '0',
      zIndex: 1000,
      backgroundColor: theme.palette.brand.legacy,
    },
    logoArea: {
      minWidth: 'auto',
      display: 'flex',
      alignItems: 'center',
      flexGrow: 1,
      justifyContent: 'center',
      [theme.breakpoints.up('md')]: {
        minWidth: theme.spacing(35),
        flexGrow: 0,
        justifyContent: 'flex-start',
      },
    },
    hamburgerButton: {
      display: 'flex',
      marginRight: theme.spacing(1),
      color: theme.palette.common.white,
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
    searchBarWrapper: {
      display: 'none',
      minWidth: 650,
      [theme.breakpoints.up('md')]: {
        display: 'block',
      },
    },
  }),
);
function TopicPageHeader({ topics }: TopicPageHeaderProps): ReactElement | null {
  const baseRoute = useBaseRoute();
  const classes = useStyles();
  const history = useHistory();
  const { setMobileNavOpen, isMobile } = useMobileNav();

  const { setContentfulErrorType } = React.useContext(ContentfulErrorContext);

  const handleClick = React.useCallback(
    (e: React.SyntheticEvent) => {
      const path = `${HELP_ROUTE}`;
      history.push(path);
      setContentfulErrorType(null);
      e.preventDefault();
    },
    [history],
  );

  const handleHamburgerClick = () => {
    setMobileNavOpen(true);
  };

  return (
    <div data-testid="knowledge-base-header" className={classes.header}>
      <Container maxWidth={'lg'} className={classes.container}>
        {topics && (
          <IconButton
            className={classes.hamburgerButton}
            onClick={handleHamburgerClick}
            aria-label="Open navigation menu"
            data-testid="mobile-nav-hamburger-button"
          >
            <MenuIcon />
          </IconButton>
        )}
        <div className={classes.logoArea}>
          <a onClick={handleClick}>
            <AgentNetServices width={175} />
          </a>
        </div>
        {isMobile ? (
          <SearchBarRow buttonSize="large" isMobile={isMobile} />
        ) : (
          <div className={classes.searchBarWrapper}>
            <SearchBarRow buttonSize="large" isMobile={isMobile} />
          </div>
        )}
      </Container>
    </div>
  );
}

export { TopicPageHeader };
